import React, {
  useCallback,
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useDeepCompareMemo } from 'use-deep-compare';
import { flyToButton } from '../lib/flyToButton';

export const UIStateContext = createContext();

export const VIEW_MODE_GRID = 'grid';
export const VIEW_MODE_SPEAKER = 'speaker';
export const VIEW_MODE_MOBILE = 'mobile';

export const UIStateProvider = ({
  asides = [],
  modals = [],
  customTrayComponent,
  children,
}) => {
  const [pinnedId, setPinnedId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [preferredViewMode, setPreferredViewMode] = useState(VIEW_MODE_SPEAKER);
  const [viewMode, setViewMode] = useState(preferredViewMode);
  const [showParticipantsBar, setShowParticipantsBar] = useState(true);
  const [showAside, setShowAside] = useState();
  const [activeModals, setActiveModals] = useState({});
  const [modalData, setModalData] = useState({});
  const [customCapsule, setCustomCapsule] = useState();
  const [showAutoplayFailedModal, setShowAutoplayFailedModal] = useState(false);
  const [ isFirstRender, setFirstRender ] = React.useState(true);
  // Popups opened automatically at call start: shown one at a time, in the order requested.
  const autoQueueRef = useRef([]);
  const autoActiveRef = useRef(null);


  /**
   * Decide on view mode based on input conditions.
   */
  useEffect(() => {
    if (isMobile) {
      setViewMode(VIEW_MODE_MOBILE);
    } else if (pinnedId) {
      setViewMode(VIEW_MODE_SPEAKER);
    } else {
      setViewMode(preferredViewMode);
    }
  }, [pinnedId, isMobile, preferredViewMode]);

  // data: an optional payload for this open, read back via modalData[modalName] (e.g. which
  // section of a multi-purpose modal to jump to) — cleared again on close.
  const openModal = useCallback((modalName, data) => {
    setActiveModals((prevState) => ({
      ...prevState,
      [modalName]: true,
    }));
    if (data !== undefined) {
      setModalData((prevState) => ({
        ...prevState,
        [modalName]: data,
      }));
    }
  }, []);

  const showNextAutoModal = useCallback(() => {
    if (autoActiveRef.current) return;
    const next = autoQueueRef.current.shift();
    if (!next) return;
    autoActiveRef.current = next;
    openModal(next.name);
  }, [openModal]);

  // getTarget returns the tray button that reopens the popup; closing flies the popup into it.
  // The popup's Paper must carry the `modal--<name>` class for the fly-out to find it.
  const autoOpenModal = useCallback((name, getTarget) => {
    autoQueueRef.current.push({ name, getTarget });
    showNextAutoModal();
  }, [showNextAutoModal]);

  const resetAutoModals = useCallback(() => {
    autoQueueRef.current = [];
    autoActiveRef.current = null;
  }, []);

  const closeModal = useCallback((modalName) => {
    if (!modalName) {
      resetAutoModals();
      setActiveModals({});
      setModalData({});
      return;
    }

    const auto = autoActiveRef.current;
    if (auto?.name === modalName && !auto.closing) {
      auto.closing = true;
      // Called synchronously so the popup is cloned before React applies the close.
      flyToButton(document.querySelector(`.modal--${modalName}`), auto.getTarget())
        .catch(() => { }) // a broken animation must not stall the queue
        .then(() => {
          if (autoActiveRef.current !== auto) return; // reset meanwhile, e.g. the call ended
          autoActiveRef.current = null;
          showNextAutoModal();
        });
    }

    setActiveModals((prevState) => ({
      ...prevState,
      [modalName]: false,
    }));
    setModalData((prevState) => ({
      ...prevState,
      [modalName]: undefined,
    }));
  }, [resetAutoModals, showNextAutoModal]);

  const currentModals = useDeepCompareMemo(() => activeModals, [activeModals]);

  const toggleAside = useCallback((newAside) => {
    setShowAside((p) => (p === newAside ? null : newAside));
  }, []);

  const closeAside = useCallback(() => {
    setShowAside(null);
  }, []);

  return (
    <UIStateContext.Provider
      value={{
        asides,
        modals,
        customTrayComponent,
        viewMode,
        openModal,
        closeModal,
        modalData,
        closeAside,
        showParticipantsBar,
        currentModals,
        toggleAside,
        pinnedId,
        showAside,
        setShowAside,
        setPreferredViewMode,
        setPinnedId,
        setShowParticipantsBar,
        customCapsule,
        setCustomCapsule,
        showAutoplayFailedModal,
        setShowAutoplayFailedModal,
        isMobile,
        setIsMobile,
        autoOpenModal,
        resetAutoModals,
        isFirstRender,
        setFirstRender,
      }}
    >
      {children}
    </UIStateContext.Provider>
  );
};

UIStateProvider.propTypes = {
  children: PropTypes.node,
  asides: PropTypes.arrayOf(PropTypes.func),
  modals: PropTypes.arrayOf(PropTypes.func),
  customTrayComponent: PropTypes.node,
};

export const useUIState = () => useContext(UIStateContext);
